import React, { useEffect, useRef } from 'react';
import { Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import FormikGrid from '../../../common/formik/FormikGrid';
import FormikButton from '../../../common/formik/FormikButton';
import FormikTextField from '../../../common/formik/FormikTextField';
import FormikAutocomplete from '../../../common/formik/FormikAutocomplete';
import { getExportTitle, useCreateProductTemplate } from '../Product.hooks';
import { ProductExportEntity, ProductTemplate } from '../Product.interface';

export interface ProductTemplateCreateDialogProps {
  open: boolean;
  productId: number;
  onCancel?: () => void;
  onCreate?: (template: ProductTemplate) => void;
}

const ProductTemplateCreateDialog: React.FC<ProductTemplateCreateDialogProps> =
  props => {
    const { open, productId, onCancel, onCreate } = props;
    const onCreateRef = useRef(onCreate);
    const formikRef = useRef<FormikProps<{
      name: string;
      type: string;
    }> | null>(null);
    const {
      createdProductTemplate,
      isCreating,
      createError,
      createProductTemplate
    } = useCreateProductTemplate(productId);

    const options = Object.values(ProductExportEntity)
      .filter(type => {
        return (
          type !== ProductExportEntity.eClinicalReport ||
          process.env.REACT_APP_CLIENT_ENV?.includes('eclinicalworks')
        );
      })
      .map(type => ({ value: type, label: getExportTitle(type) }));

    useEffect(() => {
      onCreateRef.current = onCreate;
    }, [onCreate]);

    useEffect(() => {
      formikRef.current?.setSubmitting(isCreating);
    }, [isCreating]);

    useEffect(() => {
      if (createdProductTemplate) {
        onCreateRef.current?.(createdProductTemplate);
      }
    }, [createdProductTemplate]);

    return (
      <Dialog open={open} onClose={onCancel}>
        <Formik
          innerRef={formik => (formikRef.current = formik)}
          initialValues={{ name: '', type: '' }}
          validationSchema={Yup.object({
            name: Yup.string().required('Name is required.'),
            type: Yup.string().required('Type is required.')
          })}
          onSubmit={form => {
            createProductTemplate(form as Partial<ProductTemplate>);
          }}
        >
          <Form autoComplete='off'>
            <DialogTitle>Create Product Template</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Enter Product template details
              </DialogContentText>
              <FormikGrid>
                <FormikTextField
                  autoFocus
                  required
                  type='text'
                  name='name'
                  label='Name'
                  placeholder='Name of template'
                />
                <FormikAutocomplete
                  name='type'
                  label='Type'
                  options={options}
                  getOptionLabel={option => option.label}
                  getOptionSelected={(option, value) => {
                    return option.value === value.value;
                  }}
                  getSelectedValue={option => option.value}
                />
              </FormikGrid>

              {createError && (
                <Alert severity='error'>
                  {createError?.message || 'Unable to save Risk details.'}
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={onCancel}>Cancel</Button>
              <FormikButton>Create</FormikButton>
            </DialogActions>
          </Form>
        </Formik>
      </Dialog>
    );
  };

export default ProductTemplateCreateDialog;
